/**
 * Wertia Dossier Finanzanalyse — Pagina principale.
 *
 * Route: /clients/:id/dossier/* (con nested sub-routes)
 *
 * Layout: Header CRM + breadcrumb + sidebar dark-brown + panel attivo.
 * Carica il dossier via useDossier (auto-crea se non esiste).
 * Pulsanti export Excel/PDF nella toolbar.
 */
import React from "react";
import { useParams, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Download, ChevronRight, ArrowLeft, FileSpreadsheet, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import DossierTopNav, { PrevNextButtons } from "../components/dossier/DossierSidebar";
import {
  DossierStartPanel, RedditoPanel, PatrimonioPanel,
  SpeseContrattiPanel, RiepilogoPanel, CoperturePanel, StrategiaPanel, ExportPanel,
} from "../components/dossier/DossierPanels";
import { useDossier, API, authHeaders } from "../components/dossier/utils";

export default function DossierPage() {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const { dossier, computed, loading, saving, error, updateSection, reimportContratti, loadDossier } = useDossier(clientId);
  const [exporting, setExporting] = React.useState(false);

  const exportExcel = async () => {
    if (!dossier?.id) return;
    setExporting(true);
    try {
      const res = await axios.get(`${API}/dossiers/${dossier.id}/export.xlsx`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dossier_${dossier.persone?.[0]?.cognome || "client"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export Excel completato");
    } catch (e) {
      toast.error("Export Excel fallito (endpoint in arrivo)");
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async () => {
    if (!dossier?.id) return;
    setExporting(true);
    try {
      const res = await axios.get(`${API}/dossiers/${dossier.id}/export.pdf`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dossier_${dossier.persone?.[0]?.cognome || "client"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export PDF completato");
    } catch (e) {
      toast.error("Export PDF fallito (endpoint in arrivo)");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-stone-500">Caricamento Dossier...</div>;
  if (error) return <div className="p-8 text-red-600">Errore: {error}</div>;
  if (!dossier) return <div className="p-8">Dossier non disponibile.</div>;

  const persona = dossier.persone?.[0] || {};
  const fullName = `${persona.nome || ""} ${persona.cognome || ""}`.trim() || "Cliente";

  return (
    <div className="bg-stone-50 min-h-[calc(100vh-3.5rem)]" data-testid="dossier-page">
      {/* Toolbar / breadcrumb */}
      <div className="bg-white border-b border-stone-200 px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-stone-500" data-testid="dossier-breadcrumb">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${clientId}`)} className="h-7 px-2" data-testid="dossier-back">
            <ArrowLeft className="w-3 h-3 mr-1" />Cliente
          </Button>
          <ChevronRight className="w-3 h-3" />
          <span>Dossier</span>
          <ChevronRight className="w-3 h-3" />
          <span className="text-stone-900 font-semibold">{fullName}</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={exportExcel} disabled={exporting} data-testid="dossier-export-excel">
            <FileSpreadsheet className="w-3 h-3 mr-1" />Excel
          </Button>
          <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting} data-testid="dossier-export-pdf">
            <FileText className="w-3 h-3 mr-1" />PDF
          </Button>
        </div>
      </div>

      {/* Menu di navigazione orizzontale (7 sezioni) */}
      <DossierTopNav clientId={clientId} dossier={dossier} />

      {/* Content + Prev/Next buttons */}
      <div className="p-6 max-w-[1600px] mx-auto">
        <Routes>
          <Route index element={<DossierStartPanel dossier={dossier} updateSection={updateSection} saving={saving} />} />
          <Route path="salario" element={<RedditoPanel dossier={dossier} computed={computed} updateSection={updateSection} saving={saving} />} />
          <Route path="spese-contratti" element={<SpeseContrattiPanel dossier={dossier} computed={computed} updateSection={updateSection} reimportContratti={reimportContratti} saving={saving} />} />
          <Route path="patrimonio" element={<PatrimonioPanel dossier={dossier} computed={computed} updateSection={updateSection} saving={saving} />} />
          <Route path="riepilogo" element={<RiepilogoPanel dossier={dossier} computed={computed} />} />
          <Route path="coperture" element={<CoperturePanel dossier={dossier} computed={computed} />} />
          <Route path="strategia" element={<StrategiaPanel dossier={dossier} computed={computed} />} />
          <Route path="export" element={<ExportPanel dossier={dossier} />} />
          <Route path="*" element={<Navigate to="" replace />} />
        </Routes>

        <PrevNextButtons clientId={clientId} />
      </div>
    </div>
  );
}
